<template>
  <v-dialog v-model="dialogVisible" max-width="1400" persistent scrollable>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Vienti Netvisoriin</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle v-if="periodLabel">
        Palkkajakso: {{ periodLabel }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert v-if="loadError" type="error" dense>{{ loadError }}</v-alert>

        <v-progress-linear v-if="loading" indeterminate />

        <v-data-table
          v-if="!loading && preview"
          v-model="selectedRows"
          :headers="headers"
          :items="rows"
          :items-per-page="50"
          item-key="rowKey"
          show-select
          show-expand
          single-expand
          :expanded.sync="expanded"
          dense
          class="netvisor-preview-table"
        >
          <template v-slot:item.totalHours="{ item }">
            <strong>{{ formatHours(item.totalHours) }}</strong>
          </template>

          <template v-slot:item.warnings="{ item }">
            <v-tooltip v-if="item.warnings && item.warnings.length" bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="warning">mdi-alert</v-icon>
              </template>
              <div v-for="(w, i) in item.warnings" :key="i">{{ w }}</div>
            </v-tooltip>
            <v-icon v-else-if="item.lastExportedAt" color="success" :title="'Viety: ' + item.lastExportedAt">
              mdi-check-circle
            </v-icon>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <v-simple-table dense class="expansion-table">
                <thead>
                  <tr>
                    <th>Päivä</th>
                    <th>Kirjauslaji</th>
                    <th>Numero</th>
                    <th>Työmaa</th>
                    <th class="text-right">Määrä</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, i) in item.lines" :key="i" :class="{ 'day-start': isNewDay(item.lines, i) }">
                    <td>{{ isNewDay(item.lines, i) ? formatDate(line.date) : '' }}</td>
                    <td>{{ line.kirjauslajiName }}</td>
                    <td>{{ line.rationumber }}</td>
                    <td>{{ line.dimensionItem || line.siteName || '-' }}</td>
                    <td class="text-right">{{ formatAmount(line) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </td>
          </template>
        </v-data-table>

        <div v-if="!loading && rows.length === 0" class="text-center pa-4">
          Ei vietäviä tunteja valitulta ajalta.
        </div>

        <v-divider class="my-4" />

        <div v-if="!loading && rows.length > 0" class="d-flex align-center">
          <div>
            Valittu: <strong>{{ selectedRows.length }}</strong> / {{ rows.length }} työntekijää
          </div>
          <v-spacer />
          <div>
            Yhteensä: <strong>{{ formatHours(selectedTotalHours) }}</strong>
            ({{ selectedTotalEntries }} kirjausta)
          </div>
        </div>

        <div v-if="exportResults" class="mt-4">
          <v-alert :type="exportResultAlertType" dense>
            Onnistui: {{ exportResults.successCount }} / {{ exportResults.totalCount }} kirjausta
            <span v-if="exportResults.failedCount > 0">({{ exportResults.failedCount }} epäonnistui)</span>
          </v-alert>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Työntekijä</th>
                <th>Päivä</th>
                <th>Kirjauksia</th>
                <th>Tila</th>
                <th>Virhe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in exportResults.results" :key="i">
                <td>{{ r.workerName }}</td>
                <td>{{ formatDate(r.date) }}</td>
                <td>{{ r.hoursCount }}</td>
                <td>
                  <v-icon v-if="r.status === 'SUCCESS'" color="success" small>mdi-check</v-icon>
                  <v-icon v-else color="error" small>mdi-alert-circle</v-icon>
                  {{ statusLabel(r.status) }}
                </td>
                <td>
                  <span v-if="r.status !== 'SUCCESS'" class="error--text">{{ r.error }}</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Sulje</v-btn>
        <v-btn
          color="primary"
          :disabled="!canExport"
          :loading="exporting"
          @click="confirmAndExport"
        >
          <v-icon left>mdi-cloud-upload</v-icon>
          Vie valitut ({{ selectedRows.length }})
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import dayjs from 'dayjs';
import netvisorApi from '@/api/netvisor';

export default {
  name: 'NetvisorExportDialog',
  props: {
    value: { type: Boolean, default: false },
    dates: { type: Array, default: () => [] },
    userIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      exporting: false,
      loadError: null,
      preview: null,
      selectedRows: [],
      expanded: [],
      exportResults: null,
      headers: [
        { text: 'Työntekijä', value: 'workerName' },
        { text: 'Tunniste', value: 'employeeIdentifier' },
        { text: 'Päiviä', value: 'totalDays', align: 'end' },
        { text: 'Kirjauksia', value: 'totalEntries', align: 'end' },
        { text: 'Tunnit yht.', value: 'totalHours', align: 'end' },
        { text: '', value: 'warnings', sortable: false, width: 40 },
        { text: '', value: 'data-table-expand' },
      ],
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
    periodLabel() {
      if (!this.dates || this.dates.length === 0) return null;
      const start = dayjs(this.dates[0]).format('D.M.YYYY');
      const end = this.dates[1] ? dayjs(this.dates[1]).format('D.M.YYYY') : start;
      return `${start} – ${end}`;
    },
    rows() {
      if (!this.preview || !this.preview.rows) return [];
      return this.preview.rows.map((r, i) => ({
        ...r,
        rowKey: `${r.workerId}-${i}`,
      }));
    },
    selectedTotalHours() {
      return this.selectedRows.reduce((sum, r) => sum + Number(r.totalHours || 0), 0);
    },
    selectedTotalEntries() {
      return this.selectedRows.reduce((sum, r) => sum + (r.totalEntries || 0), 0);
    },
    canExport() {
      return this.selectedRows.length > 0
        && this.selectedRows.every(r => (r.warnings || []).length === 0 && r.totalEntries > 0);
    },
    exportResultAlertType() {
      if (!this.exportResults) return 'info';
      if (this.exportResults.failedCount === 0) return 'success';
      if (this.exportResults.successCount === 0) return 'error';
      return 'warning';
    },
  },
  watch: {
    value(v) {
      if (v) this.loadPreview();
      else this.reset();
    },
  },
  methods: {
    reset() {
      this.preview = null;
      this.selectedRows = [];
      this.expanded = [];
      this.exportResults = null;
      this.loadError = null;
    },
    close() {
      this.dialogVisible = false;
    },
    async loadPreview() {
      this.loading = true;
      this.loadError = null;
      this.exportResults = null;
      try {
        const datesStr = (this.dates || []).map(d => {
          if (typeof d === 'string') return d;
          return d.toISOString ? d.toISOString() : String(d);
        });
        this.preview = await netvisorApi.getPayrollPreview({
          dates: datesStr,
          userIds: this.userIds,
        });
        // Oletuksena valitse rivit joissa ei ole varoituksia ja on kirjauksia
        this.selectedRows = this.rows.filter(r => (r.warnings || []).length === 0 && r.totalEntries > 0);
      } catch (e) {
        this.loadError = 'Esikatselun lataus epäonnistui: ' + (e.response?.data?.message || e.message);
      } finally {
        this.loading = false;
      }
    },
    async confirmAndExport() {
      this.exporting = true;
      this.exportResults = null;
      try {
        const result = await netvisorApi.exportPayroll({
          periodStart: this.dates && this.dates[0] ? dayjs(this.dates[0]).format('YYYY-MM-DD') : null,
          periodEnd: this.dates && this.dates[1] ? dayjs(this.dates[1]).format('YYYY-MM-DD') : null,
          rows: this.selectedRows.map(({ rowKey, ...rest }) => rest),
        });
        this.exportResults = result;
      } catch (e) {
        this.loadError = 'Vienti epäonnistui: ' + (e.response?.data?.message || e.message);
      } finally {
        this.exporting = false;
      }
    },
    formatHours(v) {
      if (v == null || v === '') return '-';
      return Number(v).toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' h';
    },
    // Rivin määrä yksiköineen: backend kertoo yksikön (h = tunnit, kpl = päivärahat).
    formatAmount(line) {
      if (!line || line.hours == null || line.hours === '') return '-';
      const unit = line.unit || 'h';
      return Number(line.hours).toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + unit;
    },
    formatDate(d) {
      if (!d) return '-';
      return dayjs(d).format('DD.MM.YYYY');
    },
    // Tosi, jos rivi aloittaa uuden päivän (ensimmäinen rivi tai päivä vaihtuu edellisestä).
    isNewDay(lines, i) {
      return i === 0 || lines[i].date !== lines[i - 1].date;
    },
    statusLabel(status) {
      switch (status) {
        case 'SUCCESS': return 'Siirretty';
        case 'FAILED': return 'Epäonnistui';
        case 'PARTIAL': return 'Osittain';
        case 'IN_PROGRESS': return 'Käynnissä';
        default: return status;
      }
    },
  },
};
</script>

<style scoped>
.netvisor-preview-table >>> td {
  vertical-align: middle;
}
/* Poista expandoidun rivin oletuspadding jotta sisätaulu näkyy täysleveänä. */
.netvisor-preview-table >>> tr.v-data-table__expanded__content > td {
  padding: 0 !important;
  background: #fafafa;
  box-shadow: none;
}
/* Sisempi taulu: ei omaa reunusta/varjoa, kevyt erotus yläreunasta. */
.expansion-table {
  background: #fafafa;
  box-shadow: none !important;
  border-top: 1px solid #e0e0e0;
}
.expansion-table >>> th {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
}
/* Päiväryhmän erotus: paksumpi viiva kun päivä vaihtuu (ei ensimmäisen rivin yläpuolelle). */
.expansion-table >>> tbody tr.day-start:not(:first-child) td {
  border-top: 2px solid #bdbdbd;
}
</style>
